import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { ConfirmDialog } from "primereact/confirmdialog";
import { Dropdown } from "primereact/dropdown";
import { parseApiError } from "@/lib/error-handling";
import { useLazyTableParams } from "@/lib/useLazyTableParams";
import { useListPageParam } from "@/lib/useListPageParam";
import { useListSearchParam } from "@/lib/useListSearchParam";
import { confirmAsync, useToast } from "@/lib/ui";
import {
  useAdminGroupsList,
  useAdminGroupCreate,
  useAdminGroupUpdate,
  useAdminGroupDelete,
  type AdminGroupListItem,
} from "@/lib/admin-api";

const groupTypes = [
  { label: "Primary", value: "Primary" },
  { label: "Secondary", value: "Secondary" },
];

const GROUPS_PAGE_SIZE = 10;

export function GroupsList() {
  const navigate = useNavigate();
  const [globalFilter, setGlobalFilter] = useListSearchParam("q");
  const [pageFromUrl, setPageFromUrl] = useListPageParam();

  const {
    lazyParams,
    setLazyParams,
    onPage: onPageBase,
    onSort,
    resetToFirstPage: resetToFirstPageBase,
  } = useLazyTableParams({
    initialRows: GROUPS_PAGE_SIZE,
    initialSortField: "name",
    initialSortOrder: 1,
    initialFirst: (Math.max(1, pageFromUrl) - 1) * GROUPS_PAGE_SIZE,
  });

  const onPage = useCallback(
    (event: { first: number; rows: number; page?: number }) => {
      onPageBase(event);
      const newPage = (event.page ?? Math.floor(event.first / event.rows)) + 1;
      setPageFromUrl(newPage);
    },
    [onPageBase, setPageFromUrl],
  );

  const resetToFirstPage = useCallback(() => {
    resetToFirstPageBase();
    setPageFromUrl(1);
  }, [resetToFirstPageBase, setPageFromUrl]);

  useEffect(() => {
    setLazyParams((prev) => {
      const expectedFirst = (Math.max(1, pageFromUrl) - 1) * prev.rows;
      if (prev.first === expectedFirst) return prev;
      return {
        ...prev,
        first: expectedFirst,
        page: Math.max(0, pageFromUrl - 1),
      };
    });
  }, [pageFromUrl, setLazyParams]);

  const [groupDialog, setGroupDialog] = useState(false);
  const [group, setGroup] = useState<Partial<AdminGroupListItem>>({
    name: "",
    type: "Primary",
  });
  const [submitted, setSubmitted] = useState(false);
  const { toastRef, showToast } = useToast();

  const groupsQuery = useAdminGroupsList({
    page: lazyParams.page + 1,
    pageSize: lazyParams.rows,
    sortField: lazyParams.sortField,
    sortOrder: lazyParams.sortOrder,
    filterText: globalFilter,
  });
  const groups = (groupsQuery.data as any)?.data ?? [];
  const totalRecords = (groupsQuery.data as any)?.pagination?.total ?? 0;
  const loading = groupsQuery.isFetching;

  const groupCreate = useAdminGroupCreate();
  const groupUpdate = useAdminGroupUpdate();
  const groupDelete = useAdminGroupDelete();

  const onFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGlobalFilter(value);
    resetToFirstPage();
  };

  const openNew = () => {
    setGroup({
      name: "",
      type: "Primary",
    });
    setSubmitted(false);
    setGroupDialog(true);
  };

  const hideDialog = () => {
    setSubmitted(false);
    setGroupDialog(false);
  };

  const saveGroup = async () => {
    setSubmitted(true);

    if (group.name?.trim() && group.type) {
      try {
        if (group.id) {
          await groupUpdate.mutateAsync({
            params: { path: { id: group.id } },
            body: {
              name: group.name,
              type: group.type,
            },
          });
          showToast({
            severity: "success",
            summary: "Successful",
            detail: "Group Updated",
            life: 3000,
          });
        } else {
          await groupCreate.mutateAsync({
            body: {
              name: group.name!,
              type: group.type!,
            },
          });
          showToast({
            severity: "success",
            summary: "Successful",
            detail: "Group Created",
            life: 3000,
          });
        }

        setGroupDialog(false);
        setGroup({
          name: "",
          type: "Primary",
        });
      } catch (error: unknown) {
        const { message } = parseApiError(error);
        const detail = message || "Failed to save group";
        showToast({ severity: "error", summary: "Error", detail, life: 3000 });
      }
    }
  };

  const editGroup = (group: AdminGroupListItem) => {
    setGroup({ ...group });
    setGroupDialog(true);
  };

  const confirmDeleteGroup = async (group: AdminGroupListItem) => {
    const confirmed = await confirmAsync({
      message: `Are you sure you want to delete "${group.name}"?`,
      header: "Confirm",
      icon: "pi pi-exclamation-triangle",
      acceptLabel: "Delete",
      rejectLabel: "Cancel",
      defaultFocus: "reject",
    });
    if (!confirmed) return;
    await deleteGroup(group);
  };

  const deleteGroup = async (group: AdminGroupListItem) => {
    try {
      await groupDelete.mutateAsync({
        params: { path: { id: group.id } },
      });

      showToast({
        severity: "success",
        summary: "Successful",
        detail: "Group Deleted",
        life: 3000,
      });
    } catch (error: unknown) {
      const { message } = parseApiError(error);
      const detail = message || "Failed to delete group";
      showToast({ severity: "error", summary: "Error", detail, life: 3000 });
    }
  };

  const typeBodyTemplate = (rowData: AdminGroupListItem) => {
    return rowData.type || "-";
  };

  const memberCountBodyTemplate = (rowData: AdminGroupListItem) => {
    const count = (rowData as any).members?.length ?? 0;
    return count.toString();
  };

  const actionBodyTemplate = (rowData: AdminGroupListItem) => {
    return (
      <div className="flex gap-2">
        <Button
          icon="pi pi-eye"
          className="p-button-text p-button-sm"
          onClick={() => navigate(`/groups/${rowData.id}`)}
          tooltip="View"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-pencil"
          className="p-button-text p-button-sm"
          onClick={() => editGroup(rowData)}
          tooltip="Edit"
          tooltipOptions={{ position: "top" }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-text p-button-danger p-button-sm"
          onClick={() => {
            void confirmDeleteGroup(rowData);
          }}
          tooltip="Delete"
          tooltipOptions={{ position: "top" }}
        />
      </div>
    );
  };

  const groupDialogFooter = (
    <>
      <Button
        label="Cancel"
        icon="pi pi-times"
        className="p-button-text"
        onClick={hideDialog}
      />
      <Button
        label="Save"
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => {
          void saveGroup();
        }}
      />
    </>
  );

  return (
    <div className="card">
      <Toast ref={toastRef} />
      <ConfirmDialog />

      <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center mb-4 gap-4">
        <h1 className="text-2xl font-bold">Manage Groups</h1>
        <div className="flex gap-2 flex-column md:flex-row">
          <span className="p-input-icon-left w-full md:w-auto">
            <i className="pi pi-search" />
            <InputText
              value={globalFilter}
              onChange={onFilter}
              placeholder="Search groups..."
              className="w-full"
            />
          </span>
          <Button
            label="New Group"
            icon="pi pi-plus"
            onClick={openNew}
            className="w-full md:w-auto p-button-outlined"
          />
        </div>
      </div>

      <DataTable
        value={groups}
        lazy
        paginator
        rows={lazyParams.rows}
        first={lazyParams.first}
        totalRecords={totalRecords}
        onPage={onPage}
        onSort={onSort}
        sortField={lazyParams.sortField}
        sortOrder={lazyParams.sortOrder}
        loading={loading}
        responsiveLayout="scroll"
        emptyMessage="No groups found"
        className="text-sm"
      >
        <Column
          field="name"
          header="Name"
          sortable
          style={{ minWidth: "200px" }}
        />
        <Column
          field="type"
          header="Type"
          sortable
          body={typeBodyTemplate}
          style={{ minWidth: "120px" }}
        />
        <Column
          header="Members"
          body={memberCountBodyTemplate}
          style={{ minWidth: "100px" }}
        />
        <Column
          body={actionBodyTemplate}
          header="Actions"
          style={{ minWidth: "200px" }}
        />
      </DataTable>

      <Dialog
        visible={groupDialog}
        style={{ width: "450px" }}
        header={group.id ? "Edit Group" : "New Group"}
        modal
        className="p-fluid"
        footer={groupDialogFooter}
        onHide={hideDialog}
      >
        <div className="field">
          <label htmlFor="name">Name</label>
          <InputText
            id="name"
            value={group.name}
            onChange={(e) => setGroup({ ...group, name: e.target.value })}
            required
            autoFocus
            className={submitted && !group.name ? "p-invalid" : ""}
          />
          {submitted && !group.name && (
            <small className="p-error">Name is required.</small>
          )}
        </div>
        <div className="field">
          <label htmlFor="type">Type</label>
          <Dropdown
            id="type"
            value={group.type}
            options={groupTypes}
            onChange={(e) => setGroup({ ...group, type: e.value })}
            placeholder="Select Type"
            className={submitted && !group.type ? "p-invalid" : ""}
          />
          {submitted && !group.type && (
            <small className="p-error">Type is required.</small>
          )}
        </div>
      </Dialog>
    </div>
  );
}

